import {Injectable} from '@angular/core';
import * as md5 from 'md5';
import {HttpClient, HttpEventType, HttpHeaders, HttpProgressEvent} from '@angular/common/http';
import {ErrorService} from '@services/error/error.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {finalize, share, tap} from 'rxjs/operators';

export interface ISignFile {
  contentType: string;
  name: string;
}

export interface IFileUploadProgress {
  progress: number;
  name: string;
  mimeType: string;
}

@Injectable({
  providedIn: 'root'
})
export class AwsUploaderService {

  private fileUrl$ = new BehaviorSubject<{ file: File, url: string } | null>(null);
  private filesUploadProgress: Array<IFileUploadProgress> = [];
  private filesUploadProgress$ = new BehaviorSubject<Array<IFileUploadProgress>>([]);

  constructor(private http: HttpClient,
              private errorService: ErrorService) {
  }

  private getHash(value: string): string {
    const result = value + new Date().getTime();
    return md5(result);
  }

  private signFile(data: ISignFile[]): Observable<string[]> {
    const stream$ = this.http.post<string[]>('medias/sign', {files: data})
      .pipe(share());

    stream$.subscribe({error: err => this.errorService.showError(err)});
    return stream$;
  }

  public uploadToAWS(file: File, index: number): Observable<string[]> {
    const fileToSign = {contentType: file.type, name: `${this.getHash(file.name)}.${file.type.split('/')[1]}`};
    return this.signFile([fileToSign])
      .pipe(
        tap((url) => {
          this.filesUploadProgress[index] = {progress: 0, name: file.name, mimeType: file.type.split('/')[0]};
          this.uploadFile(url[0], file, index);
        }),
      );
  }

  private parseImageUrl(url: string): string {
    return url.slice(0, url.indexOf('?'));
  }

  private uploadFile(url: string, file: File, index: number): void {
    const headers = new HttpHeaders({'Content-Type': file.type});

    this.http.put(url, file, {headers, observe: 'events', reportProgress: true})
      .pipe(
        finalize(() => this.fileUrl$.next(null))
      )
      .subscribe((event) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            this.handleUploadProgress(event, index);
            break;
          case HttpEventType.Response:
            if (event.url) {
              this.fileUrl$.next({file, url: this.parseImageUrl(event.url)});
            }
            break;
        }
      });
  }

  private handleUploadProgress(event: HttpProgressEvent, index: number): void {
    const total = event.total || 0;
    const progress = Math.round(event.loaded / total * 100);
    if (!this.filesUploadProgress[index]) return;

    this.filesUploadProgress[index].progress = progress;
    this.filesUploadProgress$.next(this.filesUploadProgress);
  }

  get getFilesUploadProgress$(): Observable<Array<IFileUploadProgress>> {
    return this.filesUploadProgress$ as Observable<Array<IFileUploadProgress>>;
  }

  get filesUrl$(): Observable<{ file: File, url: string } | null> {
    return this.fileUrl$ as Observable<{ file: File, url: string } | null>;
  }

  removeProgressLoaders(): void {
    const index = this.filesUploadProgress.findIndex((item) => item.progress === 100);
    this.filesUploadProgress.splice(index, 1);
    this.filesUploadProgress$.next(this.filesUploadProgress);
  }
}
